import React, { useState } from "react";
import { useRoundData } from "@/components/strategy/useRound";
import LockedResults from "@/components/strategy/LockedResults";
import { PageHead, Loading, ErrorNote, ScorePill, Avatar, DemoBadge } from "@/components/strategy/ui";
import { PARTICIPANT_NAME } from "@/lib/strategy";

export default function Perspectives() {
  const { data, loading, locked, error, reload } = useRoundData("analysis");
  const [section, setSection] = useState("all");
  const [onlyDiff, setOnlyDiff] = useState(false);
  if (locked) return <><PageHead eyebrow="Perspectives" title="Individual perspectives" /><LockedResults /></>;
  if (loading || !data) return <Loading label="Loading perspectives" />;
  if (error) return <ErrorNote error={error} retry={reload} />;

  const pids = data.participants.map((p) => p.id);
  const rows = data.questions.filter((q) => (section === "all" || q.section === section) && (!onlyDiff || (q.score != null && q.score < 50)));

  return (
    <div className="space-y-6">
      <PageHead eyebrow="Perspectives" title="Individual perspectives" sub="Jon, Andy and Pete's answers side by side, with their reasoning. Positions are what each founder said — not agreements." actions={data.demo && <DemoBadge />} />
      <div className="flex flex-wrap items-center gap-2">
        {[{ id: "all", short: "All" }, ...data.sections].map((s) => (
          <button key={s.id} onClick={() => setSection(s.id)} className="st-pill transition-all !py-1.5 !px-3" data-testid={`persp-filter-${s.id}`}
            style={{ background: section === s.id ? "var(--st-lime-dim)" : "rgba(243,239,230,.05)", color: section === s.id ? "var(--st-lime)" : "var(--st-muted)" }}>{s.id === "all" ? "All sections" : `${s.id} · ${s.short}`}</button>
        ))}
        <label className="ml-auto flex items-center gap-2 text-sm st-muted cursor-pointer"><input type="checkbox" checked={onlyDiff} onChange={(e) => setOnlyDiff(e.target.checked)} data-testid="persp-only-diff" /> Only disagreements (&lt;50%)</label>
      </div>
      <div className="hidden md:grid grid-cols-[minmax(0,1.3fr)_repeat(3,minmax(0,1fr))_72px] gap-4 px-5 st-caps">
        <span>Question</span>{pids.map((p) => <span key={p} className="flex items-center gap-2"><Avatar pid={p} size={18} />{PARTICIPANT_NAME[p]}</span>)}<span>Align.</span>
      </div>
      <div className="space-y-3" data-testid="persp-list">
        {rows.map((q) => (
          <div key={q.id} className="st-panel p-5 grid md:grid-cols-[minmax(0,1.3fr)_repeat(3,minmax(0,1fr))_72px] gap-4" data-testid={`persp-q-${q.id}`}>
            <div>
              <p className="st-faint text-xs mb-1">{q.id} · {data.sections.find((s) => s.id === q.section)?.short}{q.critical && <span className="ml-2" style={{ color: "var(--st-red)" }}>critical</span>}</p>
              <p className="font-semibold text-sm leading-snug">{q.title}</p>
            </div>
            {pids.map((p) => (
              <div key={p} className="text-sm">
                <p className="md:hidden st-caps mb-1 flex items-center gap-2"><Avatar pid={p} size={16} />{PARTICIPANT_NAME[p]}</p>
                <p className="leading-snug">{q.answers[p]?.label || "—"}</p>
                {q.answers[p]?.reason && <p className="st-faint text-xs italic mt-1.5 leading-relaxed">“{q.answers[p].reason}”</p>}
              </div>
            ))}
            <div className="flex md:justify-end items-start"><ScorePill score={q.score} /></div>
          </div>
        ))}
        {!rows.length && <p className="st-muted text-sm">No questions match this filter.</p>}
      </div>
    </div>
  );
}
